import { Alert, AlertDescription, AlertTitle, Button, cn } from '@kalos/ui';

type LoadErrorStateProps = {
  /** What failed, for example "Couldn't load documents". */
  title: string;
  description?: string;
  /** Names the action and the object, for example "Retry loading documents". */
  retryLabel: string;
  onRetry: () => void;
  isRetrying: boolean;
  className?: string;
  /**
   * `inline`: one tinted line with Retry and no bordered alert box, for dense desktop pages such as
   * the project record. `alert` (the default) is the bordered alert used elsewhere.
   */
  variant?: 'alert' | 'inline';
};

/**
 * Shown in place of content when a load fails. Uses the danger tint so it can't be
 * mistaken for `EmptyState`, and always offers a retry.
 */
export const LoadErrorState = ({
  title,
  description = 'Check your connection, then try again.',
  retryLabel,
  onRetry,
  isRetrying,
  className,
  variant = 'alert',
}: LoadErrorStateProps) =>
  variant === 'inline' ? (
    <div
      role="alert"
      className={cn(
        'bg-danger text-danger-foreground flex flex-wrap items-center justify-between gap-2 rounded-md px-3 py-2 text-sm',
        className,
      )}
    >
      <p>
        <span className="font-medium">{title}.</span> {description}
      </p>
      <Button variant="outline" disabled={isRetrying} onClick={onRetry}>
        {isRetrying ? 'Retrying…' : retryLabel}
      </Button>
    </div>
  ) : (
    <Alert
      className={cn('bg-danger text-danger-foreground border-danger-foreground/30', className)}
    >
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
        <p>{description}</p>
        <Button variant="outline" disabled={isRetrying} onClick={onRetry}>
          {isRetrying ? 'Retrying…' : retryLabel}
        </Button>
      </AlertDescription>
    </Alert>
  );
